import React, {
    createContext,
    useContext,
    useRef,
    useCallback,
    ReactNode,
} from "react";
import { ISearchResultsCallbackOptions } from "./SearchResults";

interface SearchResultsContextType {
    registerCallback: (
        sender: string,
        callback: (options: ISearchResultsCallbackOptions) => void
    ) => void;
    unregisterCallback: (sender: string) => void;
    getCallback: (
        sender: string
    ) => ((options: ISearchResultsCallbackOptions) => void) | undefined;
}

const SearchResultsContext = createContext<
    SearchResultsContextType | undefined
>(undefined);

export const useSearchResultsContext = () => {
    const context = useContext(SearchResultsContext);
    if (!context) {
        throw new Error(
            "useSearchResultsContext must be used within a SearchResultsProvider"
        );
    }
    return context;
};

interface SearchResultsProviderProps {
    children: ReactNode;
}

export const SearchResultsProvider: React.FC<SearchResultsProviderProps> = ({
    children,
}) => {
    const callbacksRef = useRef(
        new Map<string, (options: ISearchResultsCallbackOptions) => void>()
    );

    const registerCallback = useCallback(
        (
            sender: string,
            callback: (options: ISearchResultsCallbackOptions) => void
        ) => {
            callbacksRef.current.set(sender, callback);
        },
        []
    );

    const unregisterCallback = useCallback((sender: string) => {
        callbacksRef.current.delete(sender);
    }, []);

    const getCallback = useCallback((sender: string) => {
        return callbacksRef.current.get(sender);
    }, []);

    const value = {
        registerCallback,
        unregisterCallback,
        getCallback,
    };

    return (
        <SearchResultsContext.Provider value={value}>
            {children}
        </SearchResultsContext.Provider>
    );
};
